import axios, { AxiosInstance, AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import { refreshAccessToken } from '../utils/accessTokenRefresh';
import { isOAuthPopup, isOAuthReturnLocation } from '../utils/oauthCallback';

/**
 * API Service - Axios instance configuration
 */

// Base API URL from environment or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds to allow slow endpoints (like dashboard stats on cold start)
});

// Configure automatic retries for network errors or 5xx errors
axiosRetry(apiClient, {
  retries: 2, // number of retries
  retryDelay: (retryCount) => {
    return retryCount * 1000; // time interval between retries: 1s, then 2s
  },
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || 
           (error.response?.status ? error.response.status >= 500 : false);
  }
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    
    // S'assurer que headers existe et est un objet
    if (!config.headers) {
      config.headers = {} as any;
    }
    
    // Convertir headers en objet simple si nécessaire (pour éviter les problèmes avec Headers class)
    if (config.headers && typeof config.headers === 'object' && !Array.isArray(config.headers)) {
      // Headers est déjà un objet, on peut l'utiliser directement
      if (token) {
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
    } else {
      // Si headers n'est pas un objet simple, le convertir
      const headersObj: any = {};
      if (config.headers) {
        Object.keys(config.headers as any).forEach(key => {
          headersObj[key] = (config.headers as any)[key];
        });
      }
      if (token) {
        headersObj.Authorization = `Bearer ${token}`;
      }
      config.headers = headersObj;
    }
    
    // Log warning seulement si token manquant pour dashboard layout
    if (!token && config.url?.includes('/dashboard/layout')) {
      console.warn('[API] ⚠️ Token manquant pour dashboard layout');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // If error is 401 and we haven't retried yet, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      const oauthReturn = isOAuthPopup() && isOAuthReturnLocation();
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        if (accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        if (!oauthReturn) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
      if (!oauthReturn) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;

